import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { UserPlus, Shield, RefreshCw, Pencil, Check, X } from 'lucide-react';

const ROLES = [
  { value: 'office_staff', label: 'Office Staff',  colour: 'bg-slate-100 text-slate-700' },
  { value: 'admin',        label: 'Admin',         colour: 'bg-blue-100 text-blue-700'   },
  { value: 'super_user',   label: 'Super Admin',   colour: 'bg-purple-100 text-purple-700' },
];

function roleMeta(role) {
  return ROLES.find(r => r.value === role) || ROLES[0];
}

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Add User Modal ────────────────────────────────────────────────────────────
function AddUserModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'office_staff' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await apiService.createUser({
        username: form.username.trim(),
        email:    form.email.trim().toLowerCase(),
        password: form.password || undefined,
        role:     form.role,
      });
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">Add User</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Username *</label>
            <input value={form.username} onChange={e => set('username', e.target.value)} required
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email (Microsoft account) *</label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} required
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Role *</label>
            <select value={form.role} onChange={e => set('role', e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Password <span className="text-slate-400 font-normal">(leave blank for SSO-only)</span>
            </label>
            <input type="password" value={form.password} onChange={e => set('password', e.target.value)}
              placeholder="Min 8 characters"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            <p className="text-xs text-slate-400 mt-1">SSO users don't need a password — they sign in with Microsoft.</p>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors">
              {saving ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Inline Email Editor ───────────────────────────────────────────────────────
function EmailCell({ userId, email, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value,   setValue]   = useState(email || '');
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');
  const inputRef = useRef(null);

  const open = () => { setValue(email || ''); setErr(''); setEditing(true); setTimeout(() => inputRef.current?.focus(), 0); };
  const cancel = () => { setEditing(false); setErr(''); };

  const save = async () => {
    if (!value.trim()) { setErr('Email cannot be empty'); return; }
    setSaving(true);
    try {
      await apiService.updateUserEmail(userId, value.trim().toLowerCase());
      onSaved(userId, value.trim().toLowerCase());
      setEditing(false);
    } catch (e) {
      setErr(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <input
            ref={inputRef}
            type="email"
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
            className="text-xs border border-indigo-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400 w-48"
          />
          <button onClick={save} disabled={saving} className="text-green-600 hover:text-green-700 disabled:opacity-40"><Check className="w-4 h-4" /></button>
          <button onClick={cancel} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        {err && <p className="text-xs text-red-500">{err}</p>}
      </div>
    );
  }

  return (
    <button onClick={open} className="group flex items-center gap-1.5 text-slate-500 hover:text-indigo-600 transition-colors text-left">
      <span className="text-sm">{email || <span className="text-slate-300 italic">add email</span>}</span>
      <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    </button>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function UsersPage() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showAdd,     setShowAdd]     = useState(false);
  const [saving,      setSaving]      = useState({}); // { [id]: true }
  const [error,       setError]       = useState('');

  // Redirect non-super_users
  useEffect(() => {
    if (user && user.role !== 'super_user') navigate('/', { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getUsers();
      setUsers(data);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRoleChange = async (id, role) => {
    setSaving(s => ({ ...s, [id]: true }));
    try {
      await apiService.updateUserRole(id, role);
      setUsers(us => us.map(u => u.id === id ? { ...u, role } : u));
    } catch (err) {
      setError(err.message || 'Failed to update role');
    } finally {
      setSaving(s => ({ ...s, [id]: false }));
    }
  };

  const handleEmailSaved = (id, email) => {
    setUsers(us => us.map(u => u.id === id ? { ...u, email } : u));
  };

  const handleToggleStatus = async (id, current) => {
    setSaving(s => ({ ...s, [id]: true }));
    try {
      await apiService.updateUserStatus(id, !current);
      setUsers(us => us.map(u => u.id === id ? { ...u, is_active: !current } : u));
    } catch (err) {
      setError(err.message || 'Failed to update status');
    } finally {
      setSaving(s => ({ ...s, [id]: false }));
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <Shield className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">User Management</h1>
            <p className="text-sm text-slate-500">{users.length} user{users.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors text-slate-500">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors">
            <UserPlus className="w-4 h-4" /> Add User
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">User</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Email</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Role</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Last Login</th>
                <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Active</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => {
                const meta      = roleMeta(u.role);
                const isSelf    = u.id === user?.id;
                const isSaving  = saving[u.id];
                return (
                  <tr key={u.id} className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${i === users.length - 1 ? 'border-b-0' : ''}`}>
                    {/* User */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-bold flex-shrink-0">
                          {u.username.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{u.username}</p>
                          {isSelf && <p className="text-xs text-indigo-500">You</p>}
                        </div>
                      </div>
                    </td>

                    {/* Email */}
                    <td className="px-5 py-3.5">
                      <EmailCell userId={u.id} email={u.email} onSaved={handleEmailSaved} />
                    </td>

                    {/* Role */}
                    <td className="px-5 py-3.5">
                      <select
                        value={u.role}
                        disabled={isSelf || isSaving}
                        onChange={e => handleRoleChange(u.id, e.target.value)}
                        className={`text-xs font-medium px-2.5 py-1 rounded-full border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:cursor-default ${meta.colour}`}
                      >
                        {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                      {isSelf && <p className="text-xs text-slate-400 mt-0.5">Can't change own role</p>}
                    </td>

                    {/* Last Login */}
                    <td className="px-5 py-3.5 text-slate-500">{fmtDate(u.last_login)}</td>

                    {/* Active toggle */}
                    <td className="px-5 py-3.5 text-center">
                      <button
                        disabled={isSelf || isSaving}
                        onClick={() => handleToggleStatus(u.id, u.is_active)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-40 disabled:cursor-default
                          ${u.is_active ? 'bg-green-500' : 'bg-slate-300'}`}
                        title={isSelf ? "Can't disable your own account" : u.is_active ? 'Disable account' : 'Enable account'}
                      >
                        <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${u.is_active ? 'translate-x-4' : 'translate-x-1'}`} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-slate-400 mt-4">
        Roles: <strong>Office Staff</strong> — enter payments, view members &nbsp;·&nbsp;
        <strong>Admin</strong> — void/approve payments, manual overrides &nbsp;·&nbsp;
        <strong>Super Admin</strong> — full access, user management, audit log
      </p>

      {showAdd && <AddUserModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}
